
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { 
  ArrowRight,
  ArrowLeft, 
  CreditCardIcon, 
  User, 
  ShoppingCart, 
  Bitcoin, 
  Apple, 
  BanknoteIcon,
  Loader2,
  Baby,
  CircleDollarSign,
  Wallet,
  Send,
  CircleCheck
} from 'lucide-react';
import EthereumIcon from '@/components/icons/EthereumIcon';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { StepProgress } from "@/components/ui/progress";
import { 
  Card, 
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const subscriptionPlans = [
  {
    id: "basic",
    name: "Basic",
    price: "Free",
    features: [
      "Auto-mod functionality",
      "Basic security tools",
      "Standard support"
    ]
  },
  {
    id: "plus",
    name: "Plus",
    price: "$4.99",
    features: [
      "All Basic features",
      "AI conversation capabilities",
      "Engagement tools",
      "Priority support"
    ]
  },
  {
    id: "pro",
    name: "Pro",
    price: "$9.99",
    features: [
      "All Plus features",
      "Custom commands",
      "Dedicated support",
      "Advanced analytics"
    ]
  }
];

const paymentMethods = [
  { 
    id: 'credit_card', 
    name: 'Credit / Debit Card', 
    icon: <CreditCardIcon className="h-5 w-5" />,
    description: 'Pay securely with your card',
    requiresDetails: true
  },
  { 
    id: 'paypal', 
    name: 'PayPal', 
    icon: <CreditCardIcon className="h-5 w-5" />,
    description: 'Fast and secure payment with PayPal',
    simulateRedirect: true
  },
  { 
    id: 'crypto', 
    name: 'Cryptocurrency', 
    icon: <Bitcoin className="h-5 w-5" />,
    description: 'Pay with Bitcoin, Ethereum or other cryptocurrencies',
    requiresCryptoSelection: true
  },
  { 
    id: 'apple_pay', 
    name: 'Apple Pay', 
    icon: <Apple className="h-5 w-5" />,
    description: 'Quick and easy payment with Apple Pay',
    simulateRedirect: true
  },
  { 
    id: 'bank_transfer', 
    name: 'Bank Transfer', 
    icon: <BanknoteIcon className="h-5 w-5" />,
    description: 'Direct transfer from your bank account',
    requiresDetails: true
  },
  { 
    id: 'firstborn', 
    name: 'Your firstborn', 
    icon: <Baby className="h-5 w-5" />,
    description: 'Straight from the hospital',
    alwaysFail: true
  }
];

const cryptoOptions = [
  { id: 'bitcoin', name: 'Bitcoin', icon: <Bitcoin className="h-6 w-6" />, color: 'bg-amber-500' },
  { id: 'ethereum', name: 'Ethereum', icon: <EthereumIcon className="h-6 w-6" />, color: 'bg-blue-600' },
];

const Checkout = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();

  const queryParams = new URLSearchParams(location.search);
  const planFromUrl = queryParams.get('plan') || 'plus';
  const periodFromUrl = queryParams.get('period') || 'monthly';
  
  const [step, setStep] = useState(1);
  const [selectedPlan, setSelectedPlan] = useState<string>(planFromUrl);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>('');
  const [selectedCrypto, setSelectedCrypto] = useState<string>('bitcoin');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  
  const [currentPlan, setCurrentPlan] = useState(() => {
    const foundPlan = subscriptionPlans.find(p => p.id === planFromUrl);
    return foundPlan || subscriptionPlans[1];
  });
  
  useEffect(() => {
    const plan = subscriptionPlans.find(p => p.id === selectedPlan);
    if (plan) {
      setCurrentPlan(plan);
    }
  }, [selectedPlan]);
  
  const summaryForm = useForm({
    defaultValues: {
      confirmPlan: true
    }
  });
  
  const personalInfoForm = useForm({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      discordTag: '',
      phone: '',
      country: '',
      address: '',
      city: '',
      zipCode: '',
      company: ''
    }
  });
  
  const paymentMethodForm = useForm({
    defaultValues: {
      paymentMethod: '',
    }
  });
  
  const cryptoForm = useForm({
    defaultValues: {
      cryptoType: 'bitcoin',
      email: '',
      walletAddress: '',
    }
  });
  
  const paymentDetailsForm = useForm({
    defaultValues: {
      cardNumber: '',
      cardName: '',
      expiryDate: '',
      cvv: '',
      email: '',
      accountNumber: '',
      sortCode: '',
      bankName: '',
    }
  });

  const calculatePrice = () => {
    const basePrice = currentPlan.price;
    if (basePrice === "Free") return "Free";
    const numericPrice = parseFloat(basePrice.replace("$", ""));
    if (periodFromUrl === 'yearly') {
      const yearlyPrice = ((((numericPrice - 4.99)/5)*99.99)+(((numericPrice - 9.99)/-5)*49.99)).toFixed(2);
      return `$${yearlyPrice}`;
    }
    return basePrice;
  };

  const handleNextStep = () => {
    setIsButtonLoading(true);
    
    setTimeout(() => {
      setIsButtonLoading(false);
      setStep(prev => prev + 1);
    }, 800);
  };
  
  const handlePrevStep = () => {
    setStep(prev => Math.max(prev - 1, 1));
  };
  
  const handleSummarySubmit = summaryForm.handleSubmit(() => {
    handleNextStep();
  });
  
  const handlePersonalInfoSubmit = personalInfoForm.handleSubmit((data) => {
    if (!data.firstName || !data.lastName || !data.email || !data.discordTag) {
      toast({
        title: "Required Information Missing",
        description: "Please fill out all required fields",
        variant: "destructive"
      });
      return;
    }
    
    handleNextStep();
  });
  
  const handlePaymentMethodSubmit = paymentMethodForm.handleSubmit((data) => {
    if (!data.paymentMethod) {
      toast({
        title: "Payment Method Required",
        description: "Please select a payment method",
        variant: "destructive"
      });
      return;
    }
    
    setSelectedPaymentMethod(data.paymentMethod);
    const paymentMethod = paymentMethods.find(pm => pm.id === data.paymentMethod);
    
    setIsButtonLoading(true);
    
    setTimeout(() => {
      setIsButtonLoading(false);
      
      if (paymentMethod?.simulateRedirect) {
        toast({
          title: "Redirecting...",
          description: `Redirecting to ${paymentMethod.name} for payment processing...`,
        });
        
        setTimeout(() => {
          toast({
            title: "Payment Failed",
            description: "The payment provider is currently unavailable. Please try another method.",
            variant: "destructive"
          });
        }, 2000);
        
        return;
      }
      
      if (paymentMethod?.alwaysFail) {
        toast({
          title: "Payment Failed",
          description: "Unfortunately, we cannot accept firstborns as payment method at this time.",
          variant: "destructive"
        });
        document.location.href = "https://www.youtube-nocookie.com/embed/dQw4w9WgXcQ?playlist=dQw4w9WgXcQ&autoplay=1&iv_load_policy=3&loop=1&start=";
        return;
      }
      
      handleNextStep();
    }, 1000);
  });
  
  const handlePaymentDetailsSubmit = paymentDetailsForm.handleSubmit((data) => {
    if (selectedPaymentMethod === 'credit_card' && (!data.cardNumber || !data.cardName || !data.expiryDate || !data.cvv || !data.email)) {
      toast({
        title: "Required Information Missing",
        description: "Please fill out all required fields",
        variant: "destructive"
      });
      return;
    }
    
    if (selectedPaymentMethod === 'bank_transfer' && (!data.accountNumber || !data.sortCode || !data.bankName || !data.email)) {
      toast({
        title: "Required Information Missing",
        description: "Please fill out all required fields",
        variant: "destructive"
      });
      return;
    }
    
    setIsButtonLoading(true);
    
    setTimeout(() => {
      setIsButtonLoading(false);
      setIsProcessing(true);
      
      toast({
        title: "Payment Processing",
        description: "Please wait while we process your payment...",
      });
      
      setTimeout(() => {
        setIsProcessing(false);
        setPaymentSuccess(true);
        
        toast({
          title: "Payment Successful",
          description: "Your payment has been processed successfully!",
          variant: "default",
        });
        
        handleNextStep();
      }, 3000);
    }, 1000);
  });
  
  const handleCryptoSubmit = cryptoForm.handleSubmit((data) => {
    if (!data.email) {
      toast({
        title: "Required Information Missing",
        description: "Please enter your email for payment confirmation",
        variant: "destructive"
      });
      return;
    }
    
    setSelectedCrypto(data.cryptoType);
    setIsButtonLoading(true);
    
    setTimeout(() => {
      setIsButtonLoading(false);
      setIsProcessing(true);
      
      toast({
        title: "Payment Processing",
        description: "Please wait while we process your payment...",
      });
      
      setTimeout(() => {
        setIsProcessing(false);
        setPaymentSuccess(true);
        
        toast({
          title: "Payment Successful",
          description: "Your payment has been processed successfully!",
          variant: "default",
        });
        
        handleNextStep();
      }, 3000);
    }, 1500);
  });
  
  useEffect(() => {
    if (step === 3) {
      paymentDetailsForm.reset();
      cryptoForm.reset();
    }
  }, [step, paymentDetailsForm, cryptoForm]);
  
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <form onSubmit={handleSummarySubmit}>
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 dark:text-white">Order Summary</h2>
              
              <Card className="shadow-lg bg-gradient-to-br from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
                <CardHeader className="border-b border-gray-200 dark:border-gray-700">
                  <CardTitle className="flex justify-between items-center">
                    <span className="text-lg font-semibold dark:text-white">{currentPlan.name} Plan</span>
                    <span className="text-xl font-bold text-fembot-pink">{calculatePrice()}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <div>
                    <h4 className="font-medium mb-2 dark:text-white">Features:</h4>
                    <ul className="space-y-2 text-gray-600 dark:text-gray-300">
                      {currentPlan.features.map((feature, index) => (
                        <li key={index} className="flex items-center">
                          <span className="mr-2 text-fembot-pink">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="border-t border-gray-200 dark:border-gray-700 mt-6 pt-4">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-white">Plan:</span>
                      <span className="dark:text-white">{currentPlan.name}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-white">Price:</span>
                      <span className="dark:text-white">{calculatePrice()}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-white">Billing:</span>
                      <span className="dark:text-white">{periodFromUrl === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                    </div>
                    <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                      <span className="font-bold text-lg dark:text-white">Total:</span>
                      <span className="font-bold text-lg text-fembot-pink">{calculatePrice()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="flex justify-end mt-8">
                <Button 
                  type="submit"
                  className="bg-fembot-pink hover:bg-fembot-dark-pink"
                  disabled={isButtonLoading}
                >
                  {isButtonLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        );
      
      case 2:
        return (
          <form onSubmit={handlePersonalInfoSubmit}>
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 dark:text-white">Your Information</h2>
              
              <Card className="shadow-lg bg-gradient-to-br from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
                <CardContent className="pt-6">
                  <Form {...personalInfoForm}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField
                        control={personalInfoForm.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>First Name*</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                required
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={personalInfoForm.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Last Name*</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                required
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={personalInfoForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Address*</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                type="email"
                                className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                required
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={personalInfoForm.control}
                        name="discordTag"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Discord Username*</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                required
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={personalInfoForm.control}
                        name="phone"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Phone Number</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                type="tel"
                                className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={personalInfoForm.control}
                        name="country"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Country</FormLabel>
                            <FormControl>
                              <select
                                {...field}
                                className="w-full px-4 py-2 mt-1 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-fembot-pink focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              >
                                <option value="">Select your country</option>
                                <option value="US">United States</option>
                                <option value="CA">Canada</option>
                                <option value="UK">United Kingdom</option>
                                <option value="AU">Australia</option>
                                <option value="DE">Germany</option>
                                <option value="FR">France</option>
                                <option value="JP">Japan</option>
                                <option value="Other">Other</option>
                              </select>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={personalInfoForm.control}
                        name="address"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Address</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={personalInfoForm.control}
                        name="city"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>City</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={personalInfoForm.control}
                        name="zipCode"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Zip/Postal Code</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={personalInfoForm.control}
                        name="company"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Name (Optional)</FormLabel>
                            <FormControl>
                              <Input
                                {...field}
                                className="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <div className="mt-6 text-sm text-gray-500 dark:text-gray-400">
                      * Required fields
                    </div>
                  </Form>
                </CardContent>
              </Card>
              
              <div className="flex justify-between mt-8">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevStep}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                
                <Button 
                  type="submit"
                  className="bg-fembot-pink hover:bg-fembot-dark-pink"
                  disabled={isButtonLoading}
                >
                  {isButtonLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        );
      
      case 3:
        return (
          <form onSubmit={handlePaymentMethodSubmit}>
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-6 dark:text-white">Select Payment Method</h2>
              
              <Card className="shadow-lg bg-gradient-to-br from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
                <CardHeader className="border-b border-gray-200 dark:border-gray-700">
                  <CardTitle>Payment Details</CardTitle>
                  <CardDescription>Choose how you'd like to pay</CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold mb-4 dark:text-white">Summary</h3>
                    
                    <div className="border-t border-b border-gray-200 dark:border-gray-700 py-4">
                      <div className="flex justify-between mb-2">
                        <span className="font-medium dark:text-white">Plan:</span>
                        <span className="dark:text-white">{currentPlan.name}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="font-medium dark:text-white">Price:</span>
                        <span className="dark:text-white">{calculatePrice()}</span>
                      </div>
                      <div className="flex justify-between mb-2">
                        <span className="font-medium dark:text-white">Billing:</span>
                        <span className="dark:text-white">{periodFromUrl === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold mb-4 dark:text-white">Choose Payment Method</h3>
                    
                    <Form {...paymentMethodForm}>
                      <FormField
                        control={paymentMethodForm.control}
                        name="paymentMethod"
                        render={({ field }) => (
                          <FormItem className="space-y-4">
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="space-y-4"
                              >
                                {paymentMethods.map((method) => (
                                  <div 
                                    key={method.id}
                                    className={`flex items-start space-x-3 border rounded-lg p-4 transition-all duration-200 ${
                                      field.value === method.id 
                                        ? 'border-fembot-pink bg-pink-50 dark:bg-gray-700 shadow-md' 
                                        : 'border-gray-200 dark:border-gray-700 hover:border-fembot-pink/50 hover:bg-pink-50/30'
                                    }`}
                                  >
                                    <RadioGroupItem value={method.id} id={method.id} />
                                    <div className="flex-1">
                                      <label htmlFor={method.id} className="flex items-center cursor-pointer">
                                        <div className={`flex items-center justify-center w-8 h-8 rounded-full mr-2 ${
                                          field.value === method.id ? 'bg-fembot-pink text-white' : 'bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-300'
                                        }`}>
                                          {method.icon}
                                        </div>
                                        <span className="ml-2 font-medium dark:text-white">{method.name}</span>
                                      </label>
                                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{method.description}</p>
                                    </div>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </Form>
                  </div>
                </CardContent>
                <CardFooter className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
                  <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                    <CreditCardIcon className="w-4 h-4 mr-2" />
                    Your payment information is securely processed
                  </div>
                </CardFooter>
              </Card>
              
              <div className="flex justify-between mt-8">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePrevStep}
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
                
                <Button 
                  type="submit"
                  className="bg-fembot-pink hover:bg-fembot-dark-pink"
                  disabled={!paymentMethodForm.watch("paymentMethod") || isButtonLoading}
                >
                  {isButtonLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Continue
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </form>
        );
      
      case 4:
        // Step 4: Payment Details based on payment method
        if (selectedPaymentMethod === 'crypto') {
          return (
            <form onSubmit={handleCryptoSubmit}>
              <div className="max-w-3xl mx-auto">
                <h2 className="text-2xl font-bold mb-6 dark:text-white">Cryptocurrency Payment</h2>
                
                <Card className="shadow-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30">
                  <CardHeader className="border-b border-indigo-100 dark:border-indigo-800/50">
                    <CardTitle className="flex items-center">
                      <Bitcoin className="mr-3 h-6 w-6 text-amber-500" />
                      <span>Cryptocurrency Payment</span>
                    </CardTitle>
                    <CardDescription className="text-indigo-700 dark:text-indigo-300">
                      Select your preferred cryptocurrency
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pt-6">
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 dark:text-white">Order Summary</h3>
                      
                      <div className="border-t border-b border-indigo-100 dark:border-indigo-800/50 py-4">
                        <div className="flex justify-between mb-2">
                          <span className="font-medium dark:text-white">Plan:</span>
                          <span className="dark:text-white">{currentPlan.name}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium dark:text-white">Price:</span>
                          <span className="dark:text-white">{calculatePrice()}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium dark:text-white">Billing:</span>
                          <span className="dark:text-white">{periodFromUrl === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-indigo-100 dark:border-indigo-800/50">
                          <span className="font-bold text-lg dark:text-white">Total:</span>
                          <span className="font-bold text-lg text-indigo-600 dark:text-indigo-400">{calculatePrice()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <Form {...cryptoForm}>
                      <FormField
                        control={cryptoForm.control}
                        name="cryptoType"
                        render={({ field }) => (
                          <FormItem className="mb-6">
                            <FormLabel className="text-lg font-semibold dark:text-white">Select Cryptocurrency</FormLabel>
                            <FormControl>
                              <RadioGroup
                                onValueChange={field.onChange}
                                value={field.value}
                                className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3"
                              >
                                {cryptoOptions.map((crypto) => (
                                  <div 
                                    key={crypto.id} 
                                    className={`relative rounded-xl overflow-hidden transition-all ${
                                      field.value === crypto.id 
                                        ? 'ring-2 ring-offset-2 ring-indigo-500 dark:ring-indigo-400' 
                                        : ''
                                    }`}
                                  >
                                    <div className={`p-6 ${crypto.color} bg-opacity-90 text-white`}>
                                      <RadioGroupItem 
                                        value={crypto.id} 
                                        id={crypto.id} 
                                        className="absolute top-3 right-3 border-white text-white" 
                                      />
                                      <div className="flex items-center">
                                        <div className="mr-3 bg-white/20 p-2 rounded-full">
                                          {crypto.icon}
                                        </div>
                                        <h3 className="text-xl font-bold">{crypto.name}</h3>
                                      </div>
                                      <p className="mt-2 text-sm text-white/80">
                                        Pay with {crypto.name}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </RadioGroup>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={cryptoForm.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="font-medium text-gray-700 dark:text-gray-200">
                              Email for Payment Confirmation*
                            </FormLabel>
                            <FormControl>
                              <Input 
                                {...field} 
                                type="email"
                                placeholder="your-email@example.com"
                                className="bg-white dark:bg-gray-700 w-full"
                                required
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="mt-8 p-4 bg-indigo-50 dark:bg-indigo-900/30 rounded-lg border border-indigo-100 dark:border-indigo-800/50">
                        <h3 className="font-medium text-indigo-700 dark:text-indigo-300 mb-2 flex items-center">
                          <Wallet className="h-5 w-5 mr-2" />
                          Next steps
                        </h3>
                        <p className="text-sm text-indigo-600 dark:text-indigo-200">
                          After submitting, you'll receive wallet address details for making your {selectedCrypto} transfer.
                        </p>
                      </div>
                    </Form>
                  </CardContent>
                </Card>
                
                <div className="flex justify-between mt-8">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevStep}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  
                  <Button 
                    type="submit"
                    className="bg-fembot-pink hover:bg-fembot-dark-pink"
                    disabled={isButtonLoading || isProcessing}
                  >
                    {isButtonLoading || isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isProcessing ? 'Processing Payment...' : 'Processing...'}
                      </>
                    ) : (
                      <>
                        Complete Payment
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          );
        }
        
        // For credit card
        if (selectedPaymentMethod === 'credit_card') {
          return (
            <form onSubmit={handlePaymentDetailsSubmit}>
              <div className="max-w-3xl mx-auto">
                <h2 className="text-2xl font-bold mb-6 dark:text-white">Card Payment Details</h2>
                
                <Card className="shadow-lg bg-gradient-to-br from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
                  <CardHeader className="border-b border-gray-200 dark:border-gray-700">
                    <CardTitle className="flex items-center">
                      <CreditCardIcon className="mr-3 h-5 w-5 text-fembot-pink" />
                      <span>Card Details</span>
                    </CardTitle>
                    <CardDescription>
                      Enter your credit or debit card information
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pt-6">
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 dark:text-white">Order Summary</h3>
                      
                      <div className="border-t border-b border-gray-200 dark:border-gray-700 py-4">
                        <div className="flex justify-between mb-2">
                          <span className="font-medium dark:text-white">Plan:</span>
                          <span className="dark:text-white">{currentPlan.name}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium dark:text-white">Price:</span>
                          <span className="dark:text-white">{calculatePrice()}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium dark:text-white">Billing:</span>
                          <span className="dark:text-white">{periodFromUrl === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                          <span className="font-bold text-lg dark:text-white">Total:</span>
                          <span className="font-bold text-lg text-fembot-pink">{calculatePrice()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <Form {...paymentDetailsForm}>
                      <div className="space-y-4">
                        <FormField
                          control={paymentDetailsForm.control}
                          name="cardNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Card Number*</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="1234 5678 9012 3456" required />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={paymentDetailsForm.control}
                          name="cardName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Name on Card*</FormLabel>
                              <FormControl>
                                <Input {...field} placeholder="John Doe" required />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <div className="grid grid-cols-2 gap-4">
                          <FormField
                            control={paymentDetailsForm.control}
                            name="expiryDate"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Expiry Date*</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="MM/YY" required />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          
                          <FormField
                            control={paymentDetailsForm.control}
                            name="cvv"
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>CVV*</FormLabel>
                                <FormControl>
                                  <Input {...field} type="password" placeholder="123" maxLength={4} required />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        
                        <FormField
                          control={paymentDetailsForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email for Receipt*</FormLabel>
                              <FormControl>
                                <Input {...field} type="email" placeholder="your-email@example.com" required />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </Form>
                  </CardContent>
                  <CardFooter className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                      <CreditCardIcon className="w-4 h-4 mr-2" />
                      Your payment information is securely processed
                    </div>
                  </CardFooter>
                </Card>
                
                <div className="flex justify-between mt-8">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevStep}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  
                  <Button 
                    type="submit"
                    className="bg-fembot-pink hover:bg-fembot-dark-pink"
                    disabled={isButtonLoading || isProcessing}
                  >
                    {isButtonLoading || isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isProcessing ? 'Processing Payment...' : 'Processing...'}
                      </>
                    ) : (
                      <>
                        Complete Payment
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          );
        }
        
        // For bank transfer
        if (selectedPaymentMethod === 'bank_transfer') {
          return (
            <form onSubmit={handlePaymentDetailsSubmit}>
              <div className="max-w-3xl mx-auto">
                <h2 className="text-2xl font-bold mb-6 dark:text-white">Bank Transfer Details</h2>
                
                <Card className="shadow-lg bg-gradient-to-br from-white to-pink-50 dark:from-gray-800 dark:to-gray-900">
                  <CardHeader className="border-b border-gray-200 dark:border-gray-700">
                    <CardTitle className="flex items-center">
                      <BanknoteIcon className="mr-3 h-5 w-5 text-fembot-pink" />
                      <span>Bank Details</span>
                    </CardTitle>
                    <CardDescription>
                      Enter your bank account information
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="pt-6">
                    <div className="mb-8">
                      <h3 className="text-lg font-semibold mb-4 dark:text-white">Order Summary</h3>
                      
                      <div className="border-t border-b border-gray-200 dark:border-gray-700 py-4">
                        <div className="flex justify-between mb-2">
                          <span className="font-medium dark:text-white">Plan:</span>
                          <span className="dark:text-white">{currentPlan.name}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium dark:text-white">Price:</span>
                          <span className="dark:text-white">{calculatePrice()}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                          <span className="font-medium dark:text-white">Billing:</span>
                          <span className="dark:text-white">{periodFromUrl === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                        </div>
                        <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                          <span className="font-bold text-lg dark:text-white">Total:</span>
                          <span className="font-bold text-lg text-fembot-pink">{calculatePrice()}</span>
                        </div>
                      </div>
                    </div>
                    
                    <Form {...paymentDetailsForm}>
                      <div className="space-y-4">
                        <FormField
                          control={paymentDetailsForm.control}
                          name="accountNumber"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Account Number*</FormLabel>
                              <FormControl>
                                <Input {...field} required />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={paymentDetailsForm.control}
                          name="sortCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Sort Code / Routing Number*</FormLabel>
                              <FormControl>
                                <Input {...field} required />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={paymentDetailsForm.control}
                          name="bankName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Bank Name*</FormLabel>
                              <FormControl>
                                <Input {...field} required />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={paymentDetailsForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Email for Receipt*</FormLabel>
                              <FormControl>
                                <Input {...field} type="email" placeholder="your-email@example.com" required />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </Form>
                  </CardContent>
                  <CardFooter className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-4">
                    <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                      <BanknoteIcon className="w-4 h-4 mr-2" />
                      Your banking information is securely processed
                    </div>
                  </CardFooter>
                </Card>
                
                <div className="flex justify-between mt-8">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevStep}
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  
                  <Button 
                    type="submit"
                    className="bg-fembot-pink hover:bg-fembot-dark-pink"
                    disabled={isButtonLoading || isProcessing}
                  >
                    {isButtonLoading || isProcessing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {isProcessing ? 'Processing Payment...' : 'Processing...'}
                      </>
                    ) : (
                      <>
                        Complete Payment
                        <Send className="ml-2 h-4 w-4" />
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </form>
          );
        }
        
        // Generic message for other payment methods
        return (
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 dark:text-white">Payment Details</h2>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              Please go back and select a different payment method.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={handlePrevStep}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Payment Methods
            </Button>
          </div>
        );
        
      case 5:
        return (
          <div className="max-w-3xl mx-auto text-center">
            <div className="mb-8">
              <div className="inline-block p-4 rounded-full bg-green-100 dark:bg-green-900 mb-4">
                <CircleCheck className="w-12 h-12 text-green-500 dark:text-green-400" />
              </div>
              <h2 className="text-3xl font-bold mb-4 dark:text-white">Thank You for Your Purchase!</h2>
              <p className="text-xl text-gray-600 dark:text-gray-300 mb-6">
                Welcome to the Fembot family! You're now powered by an advanced Discord bot.
              </p>
            </div>
            
            <Card className="shadow-lg bg-gradient-to-br from-white to-pink-50 dark:from-gray-800 dark:to-gray-900 mb-8">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-center text-fembot-pink">
                    <CircleDollarSign className="w-8 h-8 mr-2" />
                    <h3 className="text-xl font-semibold">Purchase Details</h3>
                  </div>
                  
                  <div className="border-t border-b border-gray-200 dark:border-gray-700 py-4">
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-white">Plan:</span>
                      <span className="dark:text-white">{currentPlan.name}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-white">Price:</span>
                      <span className="dark:text-white">{calculatePrice()}</span>
                    </div>
                    <div className="flex justify-between mb-2">
                      <span className="font-medium dark:text-white">Billing:</span>
                      <span className="dark:text-white">{periodFromUrl === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                    </div>
                  </div>
                  
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    A confirmation email has been sent to your provided email address.
                  </p>
                </div>
              </CardContent>
            </Card>
            
            <div className="space-y-4 text-center">
              <p className="text-lg text-gray-600 dark:text-gray-300">
                Get ready to experience the power of advanced AI moderation, security and engagement tools!
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-fembot-pink hover:bg-fembot-dark-pink"
              >
                Back to the Homepage
              </Button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <Layout>
      <div className="py-8 px-4 bg-gradient-to-b from-white to-pink-50 dark:from-gray-900 dark:to-gray-800 min-h-screen">
        <div className="container-custom mx-auto max-w-4xl">
          <div className="mb-10">
            <h1 className="text-3xl font-bold text-center mb-8 dark:text-white">Checkout</h1>
            
            <div className="mb-12 mx-auto max-w-2xl">
              <StepProgress steps={5} currentStep={step} className="mb-6" />
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                <div className={step >= 1 ? 'text-fembot-pink font-medium' : ''}>Summary</div>
                <div className={step >= 2 ? 'text-fembot-pink font-medium' : ''}>Information</div>
                <div className={step >= 3 ? 'text-fembot-pink font-medium' : ''}>Method</div>
                <div className={step >= 4 ? 'text-fembot-pink font-medium' : ''}>Details</div>
                <div className={step >= 5 ? 'text-fembot-pink font-medium' : ''}>Complete</div>
              </div>
            </div>
          </div>
          
          {renderStepContent()}
        </div>
      </div>
    </Layout>
  );
};

export default Checkout;
